/**
 * /submit — "Add your league to the data".
 *
 * The landing page for the Reddit funnel. Two paths behind one toggle:
 *   1. Sleeper league id  → SleeperForm  (the whole submission is the id)
 *   2. Everywhere else    → ResultsForm  (screenshots/spreadsheet + settings)
 *
 * Standalone surface like /scout and /watch — no builder, no hook chain, no
 * roster fetch, nothing that needs a league saved locally.
 *
 * 🔴 MOBILE-FIRST ON PURPOSE. Nearly all of this traffic arrives from a Reddit
 * comment on a phone, so the whole page is one narrow column. Nothing here is
 * laid out for a desktop first and squeezed down after.
 *
 * 🔴 SLEEPER IS THE DEFAULT TAB AND SHOULD STAY THAT WAY. It is the path that
 * needs nothing from the user but a paste, and its data lands in the published
 * numbers directly; the upload path costs a person a transcription. Leading
 * with the cheaper path is the whole reason it is first.
 */

import { useState } from "react";
import { theme } from "../../data/theme.js";
import { resolveRateFlag } from "../../utils/rateFlag.js";
import SleeperForm from "./SleeperForm.jsx";
import ResultsForm from "./ResultsForm.jsx";
import { C, SZ } from "./submitStyles.js";

const TABS = [
  { key: "sleeper", label: "Sleeper league" },
  { key: "results", label: "Yahoo / ESPN / anywhere else" },
];

export default function SubmitPage() {
  const [tab, setTab] = useState("sleeper");
  // Resolved once, in a state initialiser: resolveRateFlag APPLIES ?rate= and
  // persists it, so calling it on every render is not free. This is also the
  // one place /submit reads the flag, and it reads it for the cross-link below
  // and for nothing else.
  const [rateEnabled] = useState(() => {
    try {
      return resolveRateFlag();
    } catch {
      return true;                     // launched: the visible state is the safe
                                       // answer, mirroring liveBoardFlag
    }
  });

  const tabStyle = (active) => ({
    flex: "1 1 auto",
    padding: "10px 14px",
    fontSize: SZ.small,
    fontWeight: 700,
    lineHeight: 1.3,
    borderRadius: theme.radius.lg,
    border: `1px solid ${active ? C.border.brand : C.border.default}`,
    background: active ? C.bg.raised : "transparent",
    color: active ? C.text.primary : C.text.muted,
    cursor: "pointer",
  });

  return (
    <div style={{ minHeight: "100vh", background: C.bg.app, color: C.text.primary, fontFamily: theme.fonts.body }}>
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "32px 18px 64px" }}>
        <h1
          style={{
            fontFamily: theme.fonts.display,
            fontSize: SZ.hero,
            fontWeight: 900,
            lineHeight: 1.15,
            margin: 0,
            background: C.brand.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Add your league to the data
        </h1>
        <p style={{ fontSize: SZ.body, color: C.text.secondary, margin: "10px 0 18px", lineHeight: 1.5 }}>
          Played a real Sleeper auction? Paste your league URL or league ID and your room's results join
          the dataset behind the auction trends.
        </p>

        <div role="tablist" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              style={tabStyle(tab === t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "sleeper" ? <SleeperForm /> : <ResultsForm />}

        {/* 🔴 ONE OF /rate's TWO DISCOVERY SURFACES. rateFlag.js is DEFAULT ON
            since the Sep 4 2026 launch, so this link ships to everyone; it was
            held out of default view during the stealth period. `?rate=0` pulls
            it for a browser without a deploy. The /rate route itself is
            untouched: a direct link works for anybody who has one. */}
        {rateEnabled && (
          <p
            data-testid="submit-rate-link"
            style={{ marginTop: 18, fontSize: SZ.small, color: C.text.secondary, lineHeight: 1.5 }}
          >
            Already drafted on Sleeper?{" "}
            <a href="/rate" style={{ color: C.brand.accent, fontWeight: 700 }}>
              Get your whole room or just your team graded
            </a>{" "}
            against real market prices.
          </p>
        )}
      </div>
    </div>
  );
}
